import { useState } from "react";
import { Gift, Users, Settings, AlertCircle } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, EmptyState, SearchInput } from "@/components/admin/shared";
import {
  useReferrals,
  useReferralConfig,
  useUpdateReferralConfig,
} from "@/hooks/admin/useCRM";
import { formatFCFA } from "@cieda/utils";
import type { ReferralConfig } from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------

type Tab = "dashboard" | "sponsors" | "config";

// ---------------------------------------------------------------------------
// Tab nav
// ---------------------------------------------------------------------------

function TabNav({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  const tabs: Array<{ key: Tab; label: string }> = [
    { key: "dashboard", label: "Tableau de bord" },
    { key: "sponsors", label: "Parrains" },
    { key: "config", label: "Configuration" },
  ];
  return (
    <div className="flex gap-1 border-b border-stone-200">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            active === tab.key
              ? "border-amber-500 text-amber-700"
              : "border-transparent text-stone-500 hover:text-stone-800"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard tab
// ---------------------------------------------------------------------------

function DashboardTab() {
  const { data, isLoading, isError } = useReferrals();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 animate-pulse">
        {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-lg bg-stone-100" />)}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertCircle className="h-4 w-4 shrink-0" />
        Impossible de charger les données de parrainage.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <KPICard
        label="Total parrainages"
        value={data.total_referrals}
        format="number"
        icon={<Users className="h-4 w-4" />}
      />
      <KPICard
        label="Convertis"
        value={data.total_converted}
        format="number"
        icon={<Gift className="h-4 w-4" />}
      />
      <KPICard
        label="Taux de conversion"
        value={data.conversion_rate_percent}
        format="percent"
      />
      <KPICard
        label="Revenu généré"
        value={data.total_revenue_generated_fcfa}
        format="currency"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sponsors tab
// ---------------------------------------------------------------------------

function SponsorsTab() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useReferrals({ page, limit: 20, q: search || null });
  const sponsors = data?.sponsors ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      <div className="max-w-xs">
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          placeholder="Rechercher par nom ou code..."
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
        {isLoading ? (
          <div className="space-y-3 p-4 animate-pulse">
            {[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded bg-stone-100" />)}
          </div>
        ) : sponsors.length === 0 ? (
          <EmptyState
            icon={<Gift className="h-6 w-6" />}
            title="Aucun parrain trouvé"
            description="Aucun résultat ne correspond à votre recherche."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Parrain</th>
                  <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Code</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Filleuls</th>
                  <th className="hidden md:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Convertis</th>
                  <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Récompenses gagnées</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {sponsors.map((s) => (
                  <tr key={s.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-medium text-amber-800">
                          {s.full_name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">{s.full_name}</p>
                          <p className="text-xs text-stone-400">{s.phone}</p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3">
                      <code className="rounded bg-stone-100 px-2 py-0.5 text-xs font-mono text-stone-700">
                        {s.referral_code}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-stone-800">
                      {s.referee_count}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                      {s.converted_count}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                      {formatFCFA(s.rewards_earned_fcfa)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination && pagination.total > pagination.limit && (
        <div className="flex items-center justify-between text-sm text-stone-500">
          <span>
            {((pagination.page - 1) * pagination.limit) + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page === 1}
              className="rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={pagination.page * pagination.limit >= pagination.total}
              className="rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config tab
// ---------------------------------------------------------------------------

function ConfigTab() {
  const { data: config, isLoading } = useReferralConfig();
  const updateConfig = useUpdateReferralConfig();

  const [sponsorReward, setSponsorReward] = useState("");
  const [refereeReward, setRefereeReward] = useState("");
  const [minOrder, setMinOrder] = useState("");
  const [active, setActive] = useState<boolean | null>(null);

  const handleSave = () => {
    const updates: Partial<{ -readonly [K in keyof ReferralConfig]: ReferralConfig[K] }> = {};
    const sr = parseInt(sponsorReward, 10);
    const rr = parseInt(refereeReward, 10);
    const mo = parseInt(minOrder, 10);

    if (sponsorReward && !isNaN(sr)) updates.sponsor_reward_fcfa = sr;
    if (refereeReward && !isNaN(rr)) updates.referee_reward_fcfa = rr;
    if (minOrder && !isNaN(mo)) updates.min_first_order_fcfa = mo;
    if (active !== null) updates.program_active = active;

    if (Object.keys(updates).length === 0) {
      toast.info("Aucune modification à enregistrer.");
      return;
    }

    updateConfig.mutate(updates, {
      onSuccess: () => toast.success("Configuration mise à jour."),
      onError: () => toast.error("Erreur lors de la mise à jour."),
    });
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4 max-w-lg">
        {[...Array(4)].map((_, i) => <div key={i} className="h-12 rounded bg-stone-100" />)}
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-6">
      <div className="rounded-xl border border-stone-200 bg-white p-5 space-y-5">
        <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2">
          <Settings className="h-4 w-4 text-stone-400" />
          Récompenses
        </h3>

        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Récompense parrain (FCFA)
          </label>
          <input
            type="number"
            defaultValue={config?.sponsor_reward_fcfa}
            onChange={(e) => setSponsorReward(e.target.value)}
            placeholder="ex : 2000"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">Crédit accordé au parrain lors de la conversion</p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Récompense filleul (FCFA)
          </label>
          <input
            type="number"
            defaultValue={config?.referee_reward_fcfa}
            onChange={(e) => setRefereeReward(e.target.value)}
            placeholder="ex : 1000"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">Réduction sur la première commande du filleul</p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Montant minimum première commande (FCFA)
          </label>
          <input
            type="number"
            defaultValue={config?.min_first_order_fcfa}
            onChange={(e) => setMinOrder(e.target.value)}
            placeholder="ex : 5000"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">Le filleul doit commander au moins ce montant pour valider le parrainage</p>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-stone-50 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-stone-700">Programme actif</p>
            <p className="text-xs text-stone-400">Désactiver arrête l'attribution de nouveaux parrainages</p>
          </div>
          <button
            onClick={() => setActive(active === null ? !(config?.program_active) : !active)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-300 ${
              (active === null ? config?.program_active : active)
                ? "bg-amber-500"
                : "bg-stone-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                (active === null ? config?.program_active : active)
                  ? "translate-x-6"
                  : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      <Button onClick={handleSave} disabled={updateConfig.isPending}>
        {updateConfig.isPending ? "Enregistrement..." : "Enregistrer la configuration"}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Referrals() {
  const [tab, setTab] = useState<Tab>("dashboard");

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Gift className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Programme de parrainage
            </h1>
            <p className="text-sm text-stone-500">
              Suivi des parrainages et configuration des récompenses
            </p>
          </div>
        </div>

        <TabNav active={tab} onChange={setTab} />

        {tab === "dashboard" && <DashboardTab />}
        {tab === "sponsors" && <SponsorsTab />}
        {tab === "config" && <ConfigTab />}
      </div>
    </AdminLayout>
  );
}
